import type {
	SRT_Row,
	SRT_RowSelectionState,
} from '@/shared/components/master-table'
import type { OnChangeFn, SortingState } from '@tanstack/react-table'
import type { TFunction } from 'i18next'
import type { OptimizationGroupTableRow } from '../model/types'

import React, { useCallback, useMemo, useState } from 'react'
import { useIsFetching } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { formatDistanceToNow } from 'date-fns'
import { PencilIcon, PlusIcon, Settings2Icon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { getOgStatus, getStatusBadgeVariant } from '@/entities/optimization'
import { OptimizationScheduleDate } from '@/entities/optimization-schedule'

import NumberField from '@/shared/components/data-grid/fields/Number/TableCell'
import { ColumnDataType } from '@/shared/components/data-grid/types'
import {
	createSRTColumnHelper,
	MasterTable,
	useMasterReactTable,
} from '@/shared/components/master-table'
import { Operators } from '@/shared/components/query-builder'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { LinkButton } from '@/shared/components/ui/link-button'
import { RelativeTimeCard } from '@/shared/components/ui/relative-time-card'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { Tag } from '@/shared/components/ui/tag'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { OptimizationStatus } from '@/shared/generated'
import { getLangCode, getMasterTableLocale, useLocale } from '@/shared/i18n'
import { ampli } from '@/shared/lib/ampli'
import { useDrawer, useDrawers } from '@/shared/lib/drawer-manager'
import { useModals } from '@/shared/lib/modal-manager'
import { formatNumber } from '@/shared/tools/locale'
import { getFiltersQueryObject } from '@/shared/tools/location'

import { OptimizationReviewersPreview } from './OptimizationReviewersPreview'

export default function OptimizationGroupsTable({
	data,
	rowSelection,
	setRowSelection,
}: {
	data: OptimizationGroupTableRow[]
	rowSelection: SRT_RowSelectionState
	setRowSelection: OnChangeFn<SRT_RowSelectionState>
}) {
	const { t } = useTranslation()

	const [sorting, setSorting] = useState<SortingState>([
		{
			id: 'group.name',
			desc: false,
		},
	])
	const columns = useMemo(() => getColumns(t), [])
	const drawer = useDrawer('PRICING_ALERTS')
	const getRowClassName = useCallback(
		({ row }: { row: SRT_Row<OptimizationGroupTableRow> }) =>
			drawer.props?.optimizationGroupIds?.includes(row.original.group.id) &&
			drawer.isOpen
				? 'bg-blue-25 dark:bg-blue-175 h-12'
				: 'h-12',
		[drawer.props?.optimizationGroupIds]
	)
	const table = useMasterReactTable<OptimizationGroupTableRow>({
		localization: getMasterTableLocale(),
		data,
		columns,
		state: {
			sorting,
			rowSelection,
		},
		onRowSelectionChange: setRowSelection,
		enableVirtualization: true,
		defaultColumn: {
			minSize: 28,
			size: Number.MAX_SAFE_INTEGER,
			maxSize: Number.MAX_SAFE_INTEGER,
		},
		manualFiltering: true,
		manualPagination: true,
		enableRowSelection: true,
		enableSelectAll: true,
		enableMultiRowSelection: true,
		enableColumnActions: false,
		getRowId: (row) => row.group.id,
		divideX: false,
		layoutMode: 'grid',
		getRowHeight: () => 48,
		getRowClassName,
		onSortingChange: setSorting,
	})

	return <MasterTable table={table} />
}

const columnHelper = createSRTColumnHelper<OptimizationGroupTableRow>()
const getColumns = (t: TFunction) => [
	columnHelper.accessor('group.name', {
		header: t('optimization_groups_name'),
		Cell: ({ cellValue, row }) => {
			const { t } = useTranslation()

			const { showModal } = useModals()

			return (
				<div className='flex items-center justify-between'>
					<Tooltip delayDuration={400}>
						<TooltipTrigger asChild>
							<LinkButton
								asChild
								variant='brand'
								className='line-clamp-1 truncate'
							>
								<Link
									to='/p/og/$optimizationGroupId/products'
									params={{ optimizationGroupId: row.original.group.id }}
								>
									{cellValue}
								</Link>
							</LinkButton>
						</TooltipTrigger>
						<TooltipContent side='bottom' align='start'>
							{cellValue}
						</TooltipContent>
					</Tooltip>
					<Tooltip>
						<TooltipTrigger asChild>
							<Button
								data-testid='view-og-modal-trigger'
								variant='ghost'
								size='icon'
								onClick={() =>
									showModal('OPTIMIZATION_GROUP_MODAL', {
										optimizationGroupId: row.original.group.id,
									})
								}
							>
								<Settings2Icon />
							</Button>
						</TooltipTrigger>
						<TooltipContent>
							{t('settings', { defaultValue: 'Settings' })}
						</TooltipContent>
					</Tooltip>
				</div>
			)
		},
	}),
	columnHelper.accessor('sales_level_name', {
		size: 80,
		header: t('og.level'),
		Cell: ({ cellValue }) => <Tag>{cellValue}</Tag>,
	}),
	columnHelper.accessor('group.pos_ids', {
		size: 80,
		header: t('sales_unit', { defaultValue: 'Sales unit' }),
		Cell: ({ cellValue }) => {
			const value = cellValue

			return (
				<NumberField
					value={value?.length || 0}
					schema={{ type: ColumnDataType.Integer }}
				/>
			)
		},
	}),
	columnHelper.accessor((row) => getOgStatus(row?.optimization?.status), {
		size: 150,
		id: 'optimization.status',
		header: t('optimization_groups_repricing_status'),
		Cell: ({ cellValue }) => {
			const { t } = useTranslation()

			const value = cellValue

			return (
				<SyncWrapper queryKey={['optimizations']}>
					<Tag variant={getStatusBadgeVariant(value)}>{t(`opt_${value}`)}</Tag>
				</SyncWrapper>
			)
		},
	}),
	columnHelper.accessor('schedule.nearest_date', {
		size: 136,
		header: t('general_schedule'),
		Cell: (info) => {
			const { showModal } = useModals()
			const data = info.row.original.schedule

			return (
				<SyncWrapper queryKey={['schedules']}>
					{data ? (
						<div className='group flex items-center gap-2'>
							<OptimizationScheduleDate schedule={data} />
							<Button
								onClick={() =>
									showModal('OPTIMIZATION_SCHEDULE_MODAL', { id: data.id })
								}
								variant='secondary'
								size='icon-xs'
								className='hidden group-hover:inline-flex'
							>
								<PencilIcon />
							</Button>
						</div>
					) : (
						<div className='group flex items-center gap-2'>
							<span className='text-muted-foreground'>-</span>
							<Button
								onClick={() =>
									showModal('OPTIMIZATION_SCHEDULE_MODAL', {
										daraftData: {
											optimization_groups: [info.row.original.group.id],
										},
									})
								}
								variant='secondary'
								size='icon-xs'
								className='opacity-0 group-hover:opacity-100'
							>
								<PlusIcon />
							</Button>
						</div>
					)}
				</SyncWrapper>
			)
		},
	}),
	columnHelper.accessor('optimization.total_products', {
		size: 100,
		header: t('optimization_groups_total_products'),
		Cell: ({ cellValue, row }) => (
			<SyncWrapper queryKey={['optimizations']}>
				<LinkButton className='w-full' variant='brand' asChild>
					<Link
						to='/p/products'
						search={getFiltersQueryObject([
							{
								name: 'optimization_group_id',
								operation: Operators.IN,
								value: [row.original.group.id],
							},
						])}
					>
						<NumberField
							value={cellValue}
							schema={{ type: ColumnDataType.Integer }}
						/>
					</Link>
				</LinkButton>
			</SyncWrapper>
		),
	}),
	columnHelper.accessor('notifications_count', {
		size: 70,
		header: t('alerts', { defaultValue: 'Alerts' }),
		Cell: ({ cellValue, row }) => {
			const value = cellValue
			const { openDrawer } = useDrawers()

			return (
				<SyncWrapper
					queryKey={['pricing-alerts', { group_by: 'optimization_group_id' }]}
				>
					{value > 0 ? (
						<Badge
							interactive
							onClick={() => {
								ampli.alertsOpenClicked({
									alerts_of: 'og',
								})
								openDrawer('PRICING_ALERTS', {
									type: 'byOptimizationGroups',
									optimizationGroupIds: [row.original.group.id],
									drawerClassName: 'og-alerts-eastside',
								})
							}}
							className='cursor-pointer font-mono text-nowrap fade-in'
							variant='destructive'
						>
							{formatNumber(value)}
						</Badge>
					) : (
						'-'
					)}
				</SyncWrapper>
			)
		},
	}),
	columnHelper.accessor('review', {
		size: 150,
		header: t('reviewers'),
		Cell: ({ cellValue, row }) => {
			const { t } = useTranslation()

			const review = cellValue
			const { showModal } = useModals()
			const _onSendToReview = () =>
				showModal('REVIEW_MANAGEMENT', {
					optGroupId: row.original.group.id,
				})

			return (
				<SyncWrapper queryKey={['reviews']}>
					{row.original.optimization?.status === OptimizationStatus.Finished ? (
						review ? (
							<OptimizationReviewersPreview review={review} />
						) : (
							<Button onClick={_onSendToReview}>
								{t('app.to_review', { defaultValue: 'To review' })}
							</Button>
						)
					) : (
						'-'
					)}
				</SyncWrapper>
			)
		},
	}),
	columnHelper.accessor('optimization.finished_date', {
		size: 150,
		header: t('optimization_groups_last_repricing'),
		Cell: ({ cellValue }) => {
			const date = cellValue ? new Date(cellValue + 'Z') : null
			const currentLocale = useLocale()

			return (
				<SyncWrapper width={50} queryKey={['optimizations']}>
					{date && (
						<RelativeTimeCard
							variant='muted'
							date={date}
							locale={getLangCode(currentLocale)}
						>
							<span>{formatDistanceToNow(date)}</span>
						</RelativeTimeCard>
					)}
				</SyncWrapper>
			)
		},
	}),
]
const SyncWrapper = ({
	children,
	width = 60,
	queryKey,
}: {
	children: React.ReactNode
	width?: number
	queryKey: (string | object)[]
}) => {
	const isFetching = useIsFetching({
		queryKey,
		exact: true,
	})
	const random = useMemo(() => Math.random() * (1 - 0.7) + 0.7, [])

	return isFetching ? (
		<Skeleton className='h-4' style={{ width: width * random }} />
	) : (
		children
	)
}
